import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FAQ_CATEGORIES, FAQ_ITEMS } from "@/pages/help/faqData";
import { formatDateTime, formatRelativeTime } from "@/lib/format";
import { downloadCsvMatrix, type CsvCell } from "@/lib/csv";
import { buildOwnerSummary, type FaqStatRow } from "@/pages/admin/help/faqStats";
import type { FaqOptimizationRow } from "@/pages/admin/help/hooks/useFaqOptimization";

interface FaqOptimizationLedgerProps {
  rows: Record<string, FaqOptimizationRow>;
  stats: FaqStatRow[];
  savingKey: string | null;
  onEditNote: (qKey: string) => void;
  onUndo: (qKey: string) => void;
}

const CATEGORY_LABELS: Record<string, string> = FAQ_CATEGORIES.reduce(
  (acc, category) => {
    acc[category.id] = category.labelKey;
    return acc;
  },
  {} as Record<string, string>,
);

export default function FaqOptimizationLedger({
  rows,
  stats,
  savingKey,
  onEditNote,
  onUndo,
}: FaqOptimizationLedgerProps) {
  const { t } = useTranslation();
  const knownKeys = useMemo(() => new Set(FAQ_ITEMS.map((item) => item.qKey)), []);
  const categoryByKey = useMemo(() => {
    const map: Record<string, string> = {};
    FAQ_ITEMS.forEach((item) => {
      map[item.qKey] = item.cat;
    });
    return map;
  }, []);

  const questionLabel = (qKey: string) => (knownKeys.has(qKey) ? t(qKey) : t("helpFeedback.unknownQuestion"));
  const categoryLabel = (catId: string | null) => {
    const key = catId ? CATEGORY_LABELS[catId] : "";
    return key ? t(key) : t("helpFeedback.noCategory");
  };

  const records = useMemo(
    () =>
      Object.values(rows)
        .filter((row) => row.resolved)
        .sort((a, b) => (b.resolved_at ?? b.updated_at ?? "").localeCompare(a.resolved_at ?? a.updated_at ?? "")),
    [rows],
  );

  const workloads = useMemo(
    () => buildOwnerSummary(stats, t("helpFeedback.weeklyUnassigned")),
    [stats, t],
  );

  const exportWeekly = () => {
    const matrix: CsvCell[][] = [];
    matrix.push([t("helpFeedback.weeklyTitle")]);
    matrix.push([t("helpFeedback.weeklyOwnerSection")]);
    matrix.push([
      t("helpFeedback.weeklyColOwner"),
      t("helpFeedback.weeklyColPending"),
      t("helpFeedback.weeklyColResolved"),
      t("helpFeedback.weeklyColTotal"),
      t("helpFeedback.weeklyColQuestions"),
    ]);
    workloads.forEach((workload) => {
      matrix.push([
        workload.owner,
        workload.pending,
        workload.resolved,
        workload.total,
        workload.qKeys.map(questionLabel).join(" | "),
      ]);
    });
    matrix.push([]);
    matrix.push([t("helpFeedback.weeklyDetailSection")]);
    matrix.push([
      t("helpFeedback.colQuestion"),
      t("helpFeedback.colCategory"),
      t("helpFeedback.weeklyColStatus"),
      t("helpFeedback.colOwner"),
      t("helpFeedback.colNote"),
      t("helpFeedback.colNotHelpful"),
      t("helpFeedback.colRate"),
      t("helpFeedback.ledgerColBy"),
    ]);
    const details = stats
      .filter((item) => item.needsOptimize || item.resolved)
      .sort((a, b) => (b.needsOptimize ? 1 : 0) - (a.needsOptimize ? 1 : 0) || b.notHelpful - a.notHelpful);
    details.forEach((item) => {
      matrix.push([
        questionLabel(item.qKey),
        categoryLabel(item.category),
        item.resolved ? t("helpFeedback.statusResolved") : t("helpFeedback.statusOptimize"),
        item.owner ?? "",
        item.note ?? "",
        item.notHelpful,
        `${Math.round(item.rate * 100)}%`,
        item.resolvedBy ?? "",
      ]);
    });
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    downloadCsvMatrix(`faq-optimization-weekly-${stamp}.csv`, matrix);
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-base font-semibold text-foreground-950">{t("helpFeedback.ledgerTitle")}</h2>
          <p className="mt-1 text-xs text-foreground-500">{t("helpFeedback.ledgerSub")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-booklet-line" />
            </span>
            {t("helpFeedback.ledgerCount", { count: records.length })}
          </span>
          <button
            type="button"
            onClick={exportWeekly}
            disabled={workloads.length === 0}
            title={t("helpFeedback.weeklyExportTitle")}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-file-chart-line" />
            </span>
            {t("helpFeedback.weeklyExport")}
          </button>
        </div>
      </div>

      {workloads.length > 0 && (
        <div className="mt-4 rounded-lg border border-background-200 bg-background-100/70 p-4">
          <p className="text-xs font-medium text-foreground-800">{t("helpFeedback.weeklyOwnerSection")}</p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {workloads.map((workload) => (
              <span
                key={workload.owner}
                className="inline-flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-3 py-1 text-xs text-foreground-700"
              >
                <span className="font-medium text-foreground-900">{workload.owner}</span>
                {workload.pending > 0 && (
                  <span className="inline-flex items-center gap-1 text-accent-700">
                    <i className="ri-error-warning-line text-[11px]" />
                    {t("helpFeedback.weeklyPendingChip", { count: workload.pending })}
                  </span>
                )}
                {workload.resolved > 0 && (
                  <span className="inline-flex items-center gap-1 text-primary-700">
                    <i className="ri-checkbox-circle-line text-[11px]" />
                    {t("helpFeedback.weeklyResolvedChip", { count: workload.resolved })}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {records.length === 0 ? (
        <div className="mt-4 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-10 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-booklet-line text-xl" />
          </span>
          <p className="mt-3 text-sm font-medium text-foreground-950">{t("helpFeedback.ledgerEmpty")}</p>
          <p className="mt-1 text-xs text-foreground-600">{t("helpFeedback.ledgerEmptyDesc")}</p>
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-lg border border-background-200">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse text-sm">
              <thead>
                <tr className="bg-background-100 text-left text-xs text-foreground-600">
                  <th className="px-4 py-3 font-medium">{t("helpFeedback.ledgerColQuestion")}</th>
                  <th className="px-4 py-3 font-medium">{t("helpFeedback.ledgerColNote")}</th>
                  <th className="w-28 px-4 py-3 font-medium">{t("helpFeedback.ledgerColOwner")}</th>
                  <th className="w-28 px-4 py-3 font-medium">{t("helpFeedback.ledgerColBy")}</th>
                  <th className="w-36 px-4 py-3 font-medium">{t("helpFeedback.ledgerColAt")}</th>
                  <th className="w-32 px-4 py-3 text-right font-medium">{t("helpFeedback.ledgerColAction")}</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row) => (
                  <tr key={row.q_key} className="border-t border-background-200">
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground-950">{questionLabel(row.q_key)}</p>
                      <span className="mt-1 inline-flex items-center rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                        {categoryLabel(categoryByKey[row.q_key] ?? null)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {row.note ? (
                        <p className="max-w-sm text-xs leading-relaxed text-foreground-600">{row.note}</p>
                      ) : (
                        <span className="text-xs text-foreground-400">{t("helpFeedback.ledgerNoNote")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground-700">
                      {row.owner || <span className="text-foreground-400">--</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground-700">
                      {row.resolved_by || <span className="text-foreground-400">--</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground-500">
                      <span title={row.resolved_at ? formatDateTime(row.resolved_at) : ""}>
                        {row.resolved_at ? formatRelativeTime(row.resolved_at) : "--"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => onEditNote(row.q_key)}
                          disabled={savingKey === row.q_key}
                          title={t("helpFeedback.ledgerEditNote")}
                          className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-700 transition-colors hover:border-primary-300 disabled:opacity-60"
                        >
                          <span className="flex h-3 w-3 items-center justify-center">
                            <i className="ri-edit-line" />
                          </span>
                          {t("helpFeedback.ledgerEditNote")}
                        </button>
                        <button
                          type="button"
                          onClick={() => onUndo(row.q_key)}
                          disabled={savingKey === row.q_key}
                          title={t("helpFeedback.ledgerUndo")}
                          className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-500 transition-colors hover:border-accent-300 hover:text-accent-700 disabled:opacity-60"
                        >
                          <i className={savingKey === row.q_key ? "ri-loader-4-line animate-spin" : "ri-arrow-go-back-line"} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}